/**
 * The edition's cover (ADR 0035): the identity hue's tint carrying the course
 * code set large, a subject label, and the course's stroke motif cropped at
 * the right. The cover is the container; decorative by contract, the edition
 * or band beside it carries the accessible identity.
 *
 * The label is the course's department name when the payload carries one;
 * callers never invent one, so a cover without a label shows only the code.
 */
import type { ReactNode } from "react";

import { COURSE_HUE, COURSE_MOTIFS, COURSE_TINT, courseSignature, type CourseMotif } from "@/lib/course-signature";
import { cn } from "@/lib/utils";

const LATTICE_X = [250, 280, 310, 340, 370];
const LATTICE_Y = [40, 70, 100, 130, 160];

/** The six drawings, on a 400 x 200 plane sliced to the cover. */
const ART: Record<CourseMotif, ReactNode> = {
  arc: <path d="M400 140a60 60 0 0 0-60 60M400 100a100 100 0 0 0-100 100M400 60a140 140 0 0 0-140 140M400 20a180 180 0 0 0-180 180" />,
  diagonals: <path strokeLinecap="round" d="M200 230 430 0M232 230 462 0M264 230 494 0M296 230 526 0M328 230 558 0M360 230 590 0" />,
  lattice: (
    <g fill="currentColor" stroke="none">
      {LATTICE_Y.flatMap((cy) => LATTICE_X.map((cx) => <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="4" />))}
    </g>
  ),
  bars: <path strokeLinecap="round" d="M256 50v100M288 24v152M320 62v76M352 36v128M384 72v56" />,
  ring: (
    <>
      <circle cx="330" cy="100" r="36" />
      <circle cx="330" cy="100" r="68" />
      <circle cx="330" cy="100" r="100" />
      <circle cx="330" cy="100" r="132" />
    </>
  ),
  diamond: <path strokeLinejoin="round" d="M330 64l36 36-36 36-36-36zM330 32l68 68-68 68-68-68zM330 0l100 100-100 100-100-100zM330-32l132 132-132 132-132-132z" />,
};

export type EditionCoverVariant = "grid" | "band" | "phone";

const VARIANTS: Record<EditionCoverVariant, { plane: string; text: string; code: string; label: string }> = {
  /* 2:1; code 30/34. */
  grid: { plane: "aspect-[2/1] w-full", text: "left-[1.125rem] top-4", code: "text-[1.875rem]/[2.125rem]", label: "text-meta" },
  /* 232 x 88; code 22/26, label 12/16. */
  band: { plane: "h-[5.5rem] w-[14.5rem]", text: "left-4 top-[1.125rem]", code: "text-[1.375rem]/[1.625rem]", label: "text-small" },
  /* 3:1; code 24/28. */
  phone: { plane: "aspect-[3/1] w-full", text: "left-4 top-3", code: "text-[1.5rem]/[1.75rem]", label: "text-meta" },
};

export function EditionCover({
  code,
  label,
  variant = "grid",
  className,
}: {
  code: string;
  label?: string | null;
  variant?: EditionCoverVariant;
  className?: string;
}) {
  const { hue, motif } = courseSignature(code);
  const sizes = VARIANTS[variant];
  return (
    <span
      aria-hidden="true"
      data-slot="edition-cover"
      data-variant={variant}
      data-course-signature={`${hue}:${motif}`}
      className={cn("relative block shrink-0 overflow-hidden rounded-lg", sizes.plane, className)}
      style={{ background: COURSE_TINT[hue], color: COURSE_HUE[hue] }}
    >
      <svg
        viewBox="0 0 400 200"
        preserveAspectRatio="xMidYMid slice"
        fill="none"
        stroke="currentColor"
        strokeWidth={2.5}
        focusable="false"
        className="absolute inset-0 size-full"
      >
        {ART[COURSE_MOTIFS[motif] ?? COURSE_MOTIFS[0]]}
      </svg>
      <span className={cn("absolute flex flex-col gap-0.5 tabular-nums", sizes.text)}>
        <span className={cn("font-semibold tracking-[-0.02em]", sizes.code)}>{code}</span>
        {label && <span className={cn("font-semibold", sizes.label)}>{label}</span>}
      </span>
    </span>
  );
}
